import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { chunk, startCase } from 'lodash-es';
import { useState } from 'react';

import ConfigTable from '~components/Common/ConfigTable';
import { getUomDisplayOverride } from '~constants/enums';
import { useCompany } from '~hooks/useCompany/useCompany';
import { useStores } from '~store';
import UserStore from '~store/UserStore';
import { alert, AlertTypes } from '~types/AlertTypes';

const UNITS_PER_ROW = 4;

const allUnits = Object.keys(OrderUnitOfMeasure) as (keyof typeof OrderUnitOfMeasure)[];
const unitRows = chunk(allUnits, UNITS_PER_ROW);

function AllowedUnitsSetting() {
  const { userStore, toasterStore } = useStores();
  const { updateCompany } = useCompany();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState<OrderUnitOfMeasure[]>(
    () => userStore.userCompany.allowedOrderUnitsOfMeasure,
  );

  const handleToggle = async (unit: OrderUnitOfMeasure) => {
    const previousUnits = selectedUnits;
    const nextUnits = previousUnits.includes(unit)
      ? previousUnits.filter((selectedUnit) => selectedUnit !== unit)
      : [...previousUnits, unit];

    if (nextUnits.length === 0) {
      return;
    }

    setSelectedUnits(nextUnits);
    setIsSaving(true);

    try {
      await updateCompany({
        company: {
          id: userStore.userCompany.id,
          allowedOrderUnitsOfMeasure: nextUnits,
        },
      });
      toasterStore.push(
        alert(t('administration.configuration.configuration_saved'), AlertTypes.success),
      );
    } catch {
      setSelectedUnits(previousUnits);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('administration.configuration.settings.allowed_units.setting_subtitle')}
        <br />
        {t('administration.configuration.settings.allowed_units.minimum_selection')}
      </Typography>
      <ConfigTable>
        {unitRows.map((unitRow) => (
          <TableRow key={unitRow.join()}>
            {unitRow.map((unitKey) => {
              const value = OrderUnitOfMeasure[unitKey];
              const isChecked = selectedUnits.includes(value);
              const isLastSelected = isChecked && selectedUnits.length <= 1;

              return (
                <TableCell key={unitKey}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isChecked}
                        disabled={isSaving || isLastSelected}
                        onChange={() => void handleToggle(value)}
                        size="small"
                      />
                    }
                    label={
                      getUomDisplayOverride(unitKey) ?? startCase(unitKey.toLowerCase())
                    }
                  />
                </TableCell>
              );
            })}
            {Array.from({ length: UNITS_PER_ROW - unitRow.length }, (_, index) => (
              <TableCell key={`empty-${index}`} />
            ))}
          </TableRow>
        ))}
      </ConfigTable>
    </>
  );
}

export default {
  component: AllowedUnitsSetting,
  canAccess: (userStore: UserStore) => {
    return userStore.getPermissions().canEditCompany;
  },
};
